import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { businessAPI } from '../../api/businesses';
import LoadingSpinner from '../common/LoadingSpinner';
import { resolveImageUrl } from '../../utils/formatters';

const BusinessPortfolio = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [business, setBusiness] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [newImages, setNewImages] = useState([]);
  const [keptImages, setKeptImages] = useState([]);

  const maxImages = 10;

  const mode = editing ? 'edit' : 'create';

  const canAddMoreImages = useMemo(() => {
    const existingCount = mode === 'edit' ? keptImages.length : 0;
    return existingCount + newImages.length < maxImages;
  }, [keptImages.length, mode, newImages.length]);

  const resetForm = () => {
    setEditing(null);
    setTitle('');
    setComment('');
    setNewImages([]);
    setKeptImages([]);
  };

  const load = async () => {
    try {
      setLoading(true);
      const bizRes = await businessAPI.getMyBusiness();
      const biz = bizRes?.data?.data || null;
      setBusiness(biz);

      if (!biz?.id) {
        setItems([]);
        return;
      }

      const res = await businessAPI.getPortfolio(biz.id, { items_limit: 50, items_offset: 0 });
      const payload = res?.data?.data || {};
      setItems(Array.isArray(payload.items) ? payload.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPickImages = (files) => {
    const list = Array.from(files || []);
    const existingCount = mode === 'edit' ? keptImages.length : 0;
    const availableSlots = Math.max(0, maxImages - existingCount - newImages.length);
    const next = list.slice(0, availableSlots);
    if (next.length === 0) {
      toast.error(`You can upload up to ${maxImages} images`);
      return;
    }
    setNewImages((prev) => [...prev, ...next]);
  };

  const startEdit = (item) => {
    setEditing(item);
    setTitle(item?.title || '');
    setComment(item?.comment || '');
    setNewImages([]);
    setKeptImages(Array.isArray(item?.images) ? item.images : []);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!business?.id) return;

    const trimmedTitle = String(title || '').trim();
    if (!trimmedTitle) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        await businessAPI.createPortfolioItem(business.id, {
          title: trimmedTitle,
          comment: comment || '',
          portfolio_images: newImages
        });
        toast.success('Portfolio item created');
        resetForm();
        await load();
        return;
      }

      await businessAPI.updatePortfolioItem(business.id, editing.id, {
        title: trimmedTitle,
        comment: comment || '',
        images: keptImages,
        portfolio_images: newImages
      });
      toast.success('Portfolio item updated');
      resetForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save portfolio item');
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (itemId) => {
    if (!business?.id) return;
    const ok = window.confirm('Delete this portfolio item?');
    if (!ok) return;

    try {
      await businessAPI.deletePortfolioItem(business.id, itemId);
      toast.success('Deleted');
      if (editing?.id === itemId) resetForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to delete portfolio item');
    }
  };

  if (loading) return <LoadingSpinner />;

  if (!business?.id) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <p className="text-gray-700 font-medium">No business found for this account.</p>
        <p className="text-sm text-gray-500 mt-2">Create your business first, then you can add portfolio items.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Portfolio</h1>
        <p className="text-gray-600">Show your work to customers</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{mode === 'create' ? 'Add Portfolio Item' : 'Edit Portfolio Item'}</h2>
          {mode === 'edit' && (
            <button type="button" onClick={resetForm} className="btn-secondary">
              Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full input"
              placeholder="e.g. Bridal makeup, Haircut transformation"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full input"
              rows={3}
              placeholder="Short details about this work"
            />
          </div>

          {mode === 'edit' && keptImages.length > 0 && (
            <div>
              <div className="text-sm font-medium mb-2">Existing images</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {keptImages.map((img) => (
                  <div key={img} className="relative">
                    <img src={resolveImageUrl(img)} alt="Portfolio" className="h-20 w-full rounded-lg object-cover border" />
                    <button
                      type="button"
                      onClick={() => setKeptImages((prev) => prev.filter((x) => x !== img))}
                      className="absolute top-1 right-1 bg-white/90 border rounded px-2 py-0.5 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium mb-1">Images (up to {maxImages})</label>
              <div className="text-xs text-gray-500">
                {mode === 'edit' ? keptImages.length : 0}+{newImages.length}/{maxImages}
              </div>
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={!canAddMoreImages}
              onChange={(e) => onPickImages(e.target.files)}
              className="w-full input"
            />

            {newImages.length > 0 && (
              <div className="mt-3">
                <div className="text-sm font-medium mb-2">New images</div>
                <div className="flex flex-wrap gap-2">
                  {newImages.map((f, idx) => (
                    <div key={`${f.name}-${idx}`} className="flex items-center gap-2 border rounded-lg px-2 py-1">
                      <span className="text-xs text-gray-700 max-w-[180px] truncate">{f.name}</span>
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:text-red-700"
                        onClick={() => setNewImages((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving...' : (mode === 'create' ? 'Add to Portfolio' : 'Save Changes')}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Your Portfolio Items</h2>
        </div>
        <div className="p-6">
          {items.length === 0 ? (
            <div className="text-center text-gray-500">No portfolio items yet.</div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{item.title}</div>
                      {item.comment ? <div className="text-sm text-gray-600 mt-1">{item.comment}</div> : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => startEdit(item)} className="btn-secondary">
                        Edit
                      </button>
                      <button type="button" onClick={() => removeItem(item.id)} className="btn-secondary text-red-600">
                        Delete
                      </button>
                    </div>
                  </div>

                  {Array.isArray(item.images) && item.images.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
                      {item.images.slice(0, 10).map((img) => (
                        <img key={img} src={resolveImageUrl(img)} alt={item.title} className="h-20 w-full rounded-lg object-cover border" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessPortfolio;

